import type { ReactNode } from "react";
import { FilterActionsFooter } from "@design-system/Filter/FilterActionsFooter/FilterActionsFooter";
import type { PickerClassNames } from "@design-system/Picker/Picker.types";
import { styleWithVars } from "@lib/cssVars";
import styles from "./FilterPickerList.module.scss";

/**
 * Picker slot classes of a filter-panel list: the inline surface fills the
 * panel and the footer sits at its bottom edge. Pass as `classNames` to a
 * `Picker.Select inline` wrapped in `FilterPickerListFrame`.
 */
export const FILTER_PICKER_LIST_CLASS_NAMES: PickerClassNames = {
    popup: styles.surface,
    list: styles.list,
    footer: styles.footer,
};

interface FilterPickerListFrameProps {
    children: ReactNode;
    /** Caps the scrolling rows and footer (px) for hosts without a fixed-height panel. */
    maxListHeight?: number;
}

/** Full-height column around a filter-panel `Picker.Select inline`. */
export const FilterPickerListFrame = ({
    children,
    maxListHeight,
}: FilterPickerListFrameProps) => (
    <div
        className={styles.root}
        style={
            maxListHeight === undefined
                ? undefined
                : styleWithVars({
                      "--filter-picker-list-max-height": `${maxListHeight}px`,
                  })
        }
    >
        {children}
    </div>
);

interface FilterPickerListActionsProps {
    selectedCount: number;
    onDeselect: () => void;
    onReverse?: () => void;
    onApply?: () => void;
}

/** The legacy "Apply" / "Reverse" / "Deselect (N)" buttons, spaced for a `Picker.Footer`. */
export const FilterPickerListActions = ({
    selectedCount,
    onDeselect,
    onReverse,
    onApply,
}: FilterPickerListActionsProps) => (
    <div className={styles.actions}>
        <FilterActionsFooter
            selectedCount={selectedCount}
            deselectFunction={onDeselect}
            reverseFunction={onReverse}
            applyFunction={onApply}
            hideBorderAndPadding
        />
    </div>
);
